// src/pages/admin/ContactUsList.jsx
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import toastr from "toastr";
import * as Yup from "yup";
import {
  useGetAllContactUs,
  useUpdateContactUS,
} from "../../../api/contact-us";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import Paginations from "../../../components/Common/Pagination";
import Spinners from "../../../components/Common/Spinner";
import TableContainer from "../../../components/Common/TableContainer";
import { ADMIN_CONTACT_SUBJECT_FILTER_OPTIONS } from "../../../constants/contact";
import { resolvePaginatedList } from "../../../helpers/paginatedResponse";

const ContactUsList = () => {
  document.title = "Contact Us | Admin";

  // server-side state
  const [page, setPage] = useState(1); // 1-based
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");

  // Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [viewingTicket, setViewingTicket] = useState(null);

  const { mutate, data, isLoading, isPending, error } = useGetAllContactUs();
  const updateMutation = useUpdateContactUS();
  const loading = isLoading || isPending;

  const refetch = useCallback(() => {
    mutate({
      page,
      limit,
      search,
      subject: subjectFilter,
      status: statusFilter,
      role: roleFilter,
    });
  }, [mutate, page, limit, search, subjectFilter, statusFilter, roleFilter]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const { list: rows, total } = resolvePaginatedList(data);

  const onSearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const onReset = () => {
    setSearchInput("");
    setSearch("");
    setSubjectFilter("all");
    setStatusFilter("all");
    setRoleFilter("all");
    setPage(1);
    setLimit(10);
  };

  // Modal handlers
  const handleView = (ticket) => {
    setViewingTicket(ticket);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setViewingTicket(null);
  };

  const handleEdit = (ticket) => {
    setEditingTicket(ticket);
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingTicket(null);
    editFormik.resetForm();
  };

  // Edit formik
  const editFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      status: editingTicket?.status || "",
      descriptive: editingTicket?.descriptive || "",
    },
    validationSchema: Yup.object({
      status: Yup.string()
        .oneOf(
          ["contacted", "solved"],
          "Status must be either 'contacted' or 'solved'"
        )
        .required("Status is required"),
      descriptive: Yup.string().required("Descriptive field is required"),
    }),
    onSubmit: (values) => {
      const ticketId =
        editingTicket?.id || editingTicket?.slug || editingTicket?.ticketId;
      if (!ticketId) return;

      updateMutation.mutate(
        {
          ticketId: ticketId,
          payload: {
            status: values.status,
            descriptive: values.descriptive,
          },
        },
        {
          onSuccess: () => {
            toastr.success("Ticket updated successfully");
            handleCloseModal();
            refetch();
          },
        }
      );
    },
  });

  const columns = useMemo(
    () => [
      {
        header: "Full Name",
        accessorKey: "user.fullName",
        enableColumnFilter: false,
        cell: (ctx) => ctx.row.original?.user?.fullName || "-",
      },
      {
        header: "Email",
        accessorKey: "user.email",
        enableColumnFilter: false,
        cell: (ctx) => {
          const email = ctx.row.original?.user?.email || "-";
          return email !== "-" ? <a href={`mailto:${email}`}>{email}</a> : "-";
        },
      },
      {
        header: "Role",
        accessorKey: "user.role",
        enableColumnFilter: false,
        cell: (ctx) => {
          const role =
            ctx.row.original?.user?.role || ctx.row.original?.role || "-";
          if (role === "-") return "-";
          const label =
            role === "worker" ? "Worker" : role === "client" ? "Client" : role;
          return <Badge className="bg-light text-dark">{label}</Badge>;
        },
      },
      {
        header: "Subject",
        accessorKey: "subject",
        enableColumnFilter: false,
        cell: (ctx) => ctx.row.original?.subject || "-",
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (ctx) => {
          const status = ctx.row.original?.status || "-";
          if (status === "-") return "-";
          const badgeColor =
            status === "solved"
              ? "success"
              : status === "contacted"
              ? "info"
              : "secondary";
          return <Badge className={`bg-${badgeColor}`}>{status}</Badge>;
        },
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        cell: (ctx) => {
          return (
            <div className="d-flex gap-1">
              <Button
                size="sm"
                color="info"
                onClick={() => handleView(ctx.row.original)}
              >
                <i className="mdi mdi-eye me-1" />
                View
              </Button>
              <Button
                size="sm"
                color="primary"
                onClick={() => handleEdit(ctx.row.original)}
              >
                <i className="mdi mdi-pencil me-1" />
                Edit
              </Button>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Support" breadcrumbItem="Contact Us" />

        <Row className="mb-3 g-2 align-items-center">
          <Col md={4}>
            <Input
              type="text"
              placeholder="Search by name, email, subject, or message…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSearch();
              }}
            />
          </Col>
          <Col md={2}>
            <Input
              type="select"
              value={subjectFilter}
              onChange={(e) => {
                setSubjectFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">All Subjects</option>
              {ADMIN_CONTACT_SUBJECT_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Input>
          </Col>
          <Col md={2}>
            <Input
              type="select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">All Status</option>
              <option value="true">Solved</option>
              <option value="false">Contacted</option>
            </Input>
          </Col>
          <Col md={2}>
            <Input
              type="select"
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">All Roles</option>
              <option value="client">Client</option>
              <option value="worker">Worker</option>
            </Input>
          </Col>
          <Col md="auto">
            <Button color="primary" onClick={onSearch} disabled={loading}>
              <i className="mdi mdi-magnify me-1" />
              Search
            </Button>
          </Col>
        </Row>

        <Row className="mb-3 g-2 align-items-center">
          <Col md="auto" className="ms-auto d-flex gap-2 align-items-center">
            <Button color="light" onClick={onReset} disabled={loading}>
              <i className="mdi mdi-backup-restore me-1" />
              Reset
            </Button>
            {/* Page size */}
            <Input
              type="select"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              style={{ width: "auto", minWidth: "100px" }}
            >
              {[10, 20].map((n) => (
                <option key={n} value={n}>
                  Show {n}
                </option>
              ))}
            </Input>
            <Button color="light" onClick={refetch} disabled={loading}>
              <i className="mdi mdi-refresh" />
            </Button>
          </Col>
        </Row>

        <Card>
          <CardBody>
            {loading && rows.length === 0 ? (
              <Spinners setLoading={() => {}} />
            ) : error ? (
              <Alert color="danger" className="mb-0">
                {error?.response?.data?.message ||
                  error?.message ||
                  "Failed to load tickets."}
              </Alert>
            ) : (
              <>
                <TableContainer
                  columns={columns}
                  data={rows}
                  // server-controlled
                  isPagination={false}
                  isCustomPageSize={false}
                  isGlobalFilter={false}
                  isJobListGlobalFilter={false}
                  tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
                  theadClass="table-light"
                  divClassName="table-responsive"
                />

                <Paginations
                  total={total}
                  perPageData={limit}
                  currentPage={page}
                  setCurrentPage={setPage}
                  isShowingPageLength
                  paginationDiv="dataTables_paginate paging_simple_numbers pagination-rounded d-flex justify-content-end mt-3"
                  paginationClass="pagination"
                />
              </>
            )}
          </CardBody>
        </Card>

        {/* Detail Modal */}
        <Modal
          isOpen={showDetailModal}
          toggle={handleCloseDetailModal}
          size="lg"
          centered
        >
          <ModalHeader toggle={handleCloseDetailModal}>
            Contact Us Details
          </ModalHeader>
          <ModalBody>
            {viewingTicket && (
              <Row>
                <Col sm={6} className="mb-3">
                  <p className="text-muted small mb-1">Full Name</p>
                  <div className="mb-0">
                    {viewingTicket?.user?.fullName || "-"}
                  </div>
                </Col>
                <Col sm={6} className="mb-3">
                  <p className="text-muted small mb-1">Email</p>
                  <div className="mb-0">
                    {viewingTicket?.user?.email ? (
                      <a href={`mailto:${viewingTicket.user.email}`}>
                        {viewingTicket.user.email}
                      </a>
                    ) : (
                      "-"
                    )}
                  </div>
                </Col>
                <Col sm={6} className="mb-3">
                  <p className="text-muted small mb-1">Role</p>
                  <div className="mb-0">
                    {(() => {
                      const role =
                        viewingTicket?.user?.role || viewingTicket?.role || "-";
                      if (role === "-") return "-";
                      const label =
                        role === "worker"
                          ? "Worker"
                          : role === "client"
                          ? "Client"
                          : role;
                      return (
                        <Badge className="bg-light text-dark">{label}</Badge>
                      );
                    })()}
                  </div>
                </Col>
                <Col sm={6} className="mb-3">
                  <p className="text-muted small mb-1">Status</p>
                  <div className="mb-0">
                    {(() => {
                      const status = viewingTicket?.status || "-";
                      if (status === "-") return "-";
                      const badgeColor =
                        status === "solved"
                          ? "success"
                          : status === "contacted"
                          ? "info"
                          : "secondary";
                      return (
                        <Badge className={`bg-${badgeColor}`}>{status}</Badge>
                      );
                    })()}
                  </div>
                </Col>
                <Col md={12} className="mb-3">
                  <p className="text-muted small mb-1">Subject</p>
                  <div className="mb-0 text-break">
                    {viewingTicket?.subject || "-"}
                  </div>
                </Col>
                <Col md={12} className="mb-3">
                  <p className="text-muted small mb-1">Message</p>
                  <div
                    className="mb-0 text-break"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {viewingTicket?.message || "-"}
                  </div>
                </Col>
                <Col md={12}>
                  <p className="text-muted small mb-1">Descriptive</p>
                  <div
                    className="mb-0 text-break"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {viewingTicket?.descriptive || "-"}
                  </div>
                </Col>
              </Row>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleCloseDetailModal}>
              Close
            </Button>
            <Button
              color="primary"
              onClick={() => {
                handleCloseDetailModal();
                handleEdit(viewingTicket);
              }}
            >
              <i className="mdi mdi-pencil me-1" />
              Edit
            </Button>
          </ModalFooter>
        </Modal>

        {/* Edit Modal */}
        <Modal isOpen={showEditModal} toggle={handleCloseModal} size="lg">
          <ModalHeader toggle={handleCloseModal}>
            Update Support Ticket
          </ModalHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              editFormik.handleSubmit();
              return false;
            }}
          >
            <ModalBody>
              <Row>
                <Col md={12} className="mb-3">
                  <Label className="form-label">Status *</Label>
                  <Input
                    type="select"
                    name="status"
                    value={editFormik.values.status}
                    onChange={editFormik.handleChange}
                    onBlur={editFormik.handleBlur}
                    invalid={
                      editFormik.touched.status && !!editFormik.errors.status
                    }
                  >
                    <option value="">Select Status</option>
                    <option value="contacted">Contacted</option>
                    <option value="solved">Solved</option>
                  </Input>
                  <FormFeedback>{editFormik.errors.status}</FormFeedback>
                </Col>

                <Col md={12}>
                  <Label className="form-label">Descriptive *</Label>
                  <Input
                    type="textarea"
                    rows="6"
                    name="descriptive"
                    placeholder="Enter descriptive information..."
                    value={editFormik.values.descriptive}
                    onChange={editFormik.handleChange}
                    onBlur={editFormik.handleBlur}
                    invalid={
                      editFormik.touched.descriptive &&
                      !!editFormik.errors.descriptive
                    }
                  />
                  <FormFeedback>{editFormik.errors.descriptive}</FormFeedback>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button
                type="button"
                color="secondary"
                onClick={handleCloseModal}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? "Updating..." : "Update"}
              </Button>
            </ModalFooter>
          </form>
        </Modal>
      </div>
    </div>
  );
};

export default ContactUsList;
